import { navItems } from "~/constants/sidebar";
import Button from "~/components/ui/Button";
import { isFontAwesomeIcon, SVG } from "~/components/ui/SVG";
// @ts-ignore
import LogoSvg from "~/assets/svg/logo.svg?react";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import * as S from "./Sidebar.style";
import {
  ChangeEvent,
  createElement,
  FC,
  Fragment,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { paths } from "~/router/paths";
import { faBars } from "@fortawesome/pro-light-svg-icons";
import { StyledLink } from "~/components/ui/Styled/Link";
import FlexContainer from "~/components/ui/FlexContainer";
import { useQueryClient } from "@tanstack/react-query";
import { IMPORT_LIST } from "~/api/dropshipping/types";
import { getImportList } from "~/api/dropshipping/requests";

import { useLocation } from "react-router-dom";
import { useAccount } from "~/hooks/useAccount";
import { Notifications } from "~/components/notifications";
import { ISidebarCount } from "~/api/sidebarCounts/types";
import { INavItem, ISidebarIcon } from "~/types/routing";
import { useReconnectAliexpress } from "~/hooks/useReconnectAliexpress";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";
import theme from "~/constants/theme";
import { useLanguageSync } from "~/hooks/useLanguageSync";

// Lazy-load HelpCenterModal — only shown when user clicks the Help item.
const HelpCenterModal = lazy(() =>
  import("~/components/helpCenterModal").then((m) => ({ default: m.HelpCenterModal }))
);

// Track whether Userpilot has been initialized so we only call initialize() once.
let userpilotInitialized = false;

export const Brand = () => {
  return (
    <S.LinkContainer>
      <StyledLink to={paths.app.home}>
        <S.BrandContainer>
          <LogoSvg style={{ height: 40, width: "110px" }} />
        </S.BrandContainer>
      </StyledLink>
    </S.LinkContainer>
  );
};

interface MenuItemProps extends INavItem {
  openHelpCenter?: () => void;
  closeMobileSidebar?: () => void;
}

const MenuItem = (props: MenuItemProps) => {
  const {
    path,
    openInNewTab,
    icon,
    isNew,
    isOnlySVG,
    sectionLabel,
    namePath,
    getCountQuery,
    showHelpCenterModal,
    openHelpCenter,
    hideOnMobile,
    closeMobileSidebar,
    onClick,
  } = props;
  const { t } = useTranslation();
  const { data } = getCountQuery ? getCountQuery() : { data: { count: 0 } };
  const { store } = useStore();
  const { currentLanguage } = useLanguageSync();

  const isNonEnglish = currentLanguage !== "en";

  if (!!store && namePath === "nav.connect-store") {
    return null;
  }

  return (
    <Fragment key={path}>
      {sectionLabel ? (
        <S.MenuItemTitle>{t(sectionLabel)}</S.MenuItemTitle>
      ) : null}
      <S.NavItem
        hideOnMobile={hideOnMobile}
        style={{
          textDecoration: "none",
          width: "100%",
        }}
        to={path}
        key={path}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        onClick={(e: ChangeEvent<MouseEvent>) => {
          closeMobileSidebar?.();
          if (showHelpCenterModal && openHelpCenter) {
            e.preventDefault();
            openHelpCenter();
          }

          if (onClick) {
            e.preventDefault();
            onClick();
          }
        }}>
        {({ isActive }: { isActive: boolean }) => (
          <Button
            variant={isActive ? "filled" : "default"}
            color={isActive ? "primary" : "secondary"}
            fontWeight={isActive ? 600 : 500}
            bgColor={isActive ? "primaryLight" : "background"}
            radius={0.75}
            gap={"26px"}
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              position: "relative",
            }}
            justifyContent="flex-start">
            {icon && (
              <SidebarIcon
                icon={icon}
                isOnlySVG={isOnlySVG}
                isActive={isActive}
              />
            )}
            <span data-nonenglish={isNonEnglish} className="sidebar-item-label">
              {" "}
              {t(namePath)}{" "}
            </span>
            {!!data && !!(data as ISidebarCount).count && (
              <S.Count>{(data as ISidebarCount).count as number}</S.Count>
            )}

            {isNew && (
              <S.NewContainer>
                <S.NewText>New</S.NewText>
              </S.NewContainer>
            )}
          </Button>
        )}
      </S.NavItem>
    </Fragment>
  );
};

const Menu = ({ closeMobileSidebar }: { closeMobileSidebar?: () => void }) => {
  const location = useLocation();
  const { account } = useAccount();
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const { showReconnectBanner } = useReconnectAliexpress();
  const queryClient = useQueryClient();

  // Prefetch import-list data as early as possible so it's in cache by the
  // time ImportListPage renders (fires in parallel with account API fetch).
  useEffect(() => {
    if (!location.pathname.includes("import-list")) return;
    queryClient.prefetchQuery(
      [...IMPORT_LIST, { page: 1 }],
      () => getImportList({ page: 1 })
    );
  }, [location.pathname]);

  useEffect(() => {
    if (!account) return;
    // Dynamic import keeps userpilot out of the initial JS bundle.
    import("userpilot").then(({ Userpilot }) => {
      if (!userpilotInitialized) {
        Userpilot.initialize("NX-f9dabb4f");
        userpilotInitialized = true;
      }
      Userpilot.reload();
    });
  }, [account, location]);

  const settingsIndex = useMemo(
    () =>
      navItems.findIndex(({ path }) =>
        DISABLE_PAYMENTS
          ? path === paths.settings.account
          : path === paths.settings.plans
      ),
    [navItems]
  );

  const [itemsBeforeSettings, itemsAfterSettings] = useMemo(() => {
    return [
      navItems.slice(0, settingsIndex),
      navItems.slice(settingsIndex, navItems.length),
    ];
  }, []);

  const helpCenterPath = useMemo(
    () => navItems.find(({ showHelpCenterModal }) => showHelpCenterModal)?.path,
    [navItems]
  );

  return (
    <S.NavContainer>
      <FlexContainer
        height="100%"
        flexDirection="column"
        justifyContent="space-between">
        <S.MenuItemsContainer gap={0}>
          {itemsBeforeSettings.map((navItem) => (
            <MenuItem
              key={navItem.path}
              closeMobileSidebar={closeMobileSidebar}
              {...navItem}
            />
          ))}
        </S.MenuItemsContainer>
        <S.MenuItemsContainer
          marginBottom={showReconnectBanner ? 4.0 : undefined}>
          {itemsAfterSettings.map((navItem) => (
            <MenuItem
              key={navItem.path}
              openHelpCenter={() => setShowHelpCenter(true)}
              closeMobileSidebar={closeMobileSidebar}
              {...navItem}
            />
          ))}
        </S.MenuItemsContainer>
      </FlexContainer>
      <Suspense fallback={null}>
        <HelpCenterModal
          path={String(helpCenterPath)}
          isShowing={showHelpCenter}
          hide={() => setShowHelpCenter(false)}
        />
      </Suspense>
    </S.NavContainer>
  );
};

const DesktopSidebar = () => {
  return (
    <S.SidebarContainer>
      <Brand />
      <Menu />
    </S.SidebarContainer>
  );
};

const MobileSidebar = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <S.SidebarContainer isOpen={isOpen}>
      <Notifications />
      <Brand />
      <S.MobileMenuButton onClick={() => setIsOpen(!isOpen)}>
        <SVG icon={faBars} size="lg" />
      </S.MobileMenuButton>
      {isOpen && (
        <S.MobileMenu>
          <Menu closeMobileSidebar={() => setIsOpen(false)} />
        </S.MobileMenu>
      )}
    </S.SidebarContainer>
  );
};

const renderSideBarIcon = ({
  icon,
  isOnlySVG,
  isActive,
}: {
  icon: ISidebarIcon;
  isOnlySVG?: boolean;
  isActive: boolean;
}) => {
  if (isOnlySVG) {
    return <img src={icon as any} alt="Svg icon" width={20} height={20} />;
  }

  if (isFontAwesomeIcon(icon)) {
    return <SVG icon={icon as any} svgProp={{ width: 18 }} />;
  }
  return createElement(icon as FC<{ style?: React.CSSProperties }>, {
    style: { width: 20, height: 20, flexShrink: 0 },
  });
};

const SidebarIcon = ({
  icon,
  isActive,
  isOnlySVG = false,
}: {
  isActive: boolean;
  icon: ISidebarIcon;
  isOnlySVG?: boolean;
}) => <>{renderSideBarIcon({ icon, isOnlySVG, isActive })}</>;

const Sidebar = () => {
  const isTabletAndUp = useMediaQuery("tablet");
  if (isTabletAndUp) return <DesktopSidebar />;
  return <MobileSidebar />;
};

export default Sidebar;
